import { useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazySearchClientEmailsQuery, useTransferDeviceMutation } from "store/api/clientApi"
import { UITypes } from "types"
import { ReactComponent as IconTransfer } from "assets/svg/modal-icon-transfer.svg"

import getErrorMessage from "utils/getErrorMessage"
import { Spinner } from "components/common/Spinner"
import { ButtonNew } from "components/form/ButtonNew"
import { SelectDynamic } from "components/form/SelectDynamic"

import styles from "./index.module.scss"

interface Props {
  serialNumber: string
  onCancel: () => void
  onSuccess: () => void
}

export const TransferModal = ({ serialNumber, onCancel, onSuccess }: Props) => {
  const [selectedClient, setSelectedClient] = useState<UITypes.Option[]>([])
  const [errorMessage, setErrorMessage] = useState("")
  const [transferDevice, { isLoading: isTransferring }] = useTransferDeviceMutation()

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!selectedClient?.length) {
      setErrorMessage("Please select a client")
      return
    }

    setErrorMessage("")

    try {
      const result = await transferDevice({
        sn: serialNumber,
        email: selectedClient[0].label,
      })

      const error = getErrorMessage(result?.error)

      if (error) {
        toast.error(error)
        setErrorMessage(error)
      } else {
        toast.success("Device transferred successfully!")
        onSuccess()
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Transfer failed"
      toast.error(errorMsg)
      setErrorMessage(errorMsg)
    }
  }

  const isConfirmDisabled = !selectedClient?.length || isTransferring

  const handleViewPortClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    onCancel()
  }

  const renderNoOptionsMessage = () => {
    return <div className={styles.noOptionsMessage}>Search clients</div>
  }

  const addCustomOptionRenderer = (searchValue: string) => {
    return (
      <>
        <span className={styles.plusIcon}>+</span>Create New account for &#34;{searchValue}&#34;
      </>
    )
  }

  const searchFn = (option: UITypes.Option, inputValue: string) => {
    return option.label.toLowerCase().includes(inputValue.toLowerCase())
  }

  return (
    <>
      {createPortal(
        <>
          <div className={styles.transferModalViewport} onClick={handleViewPortClick}></div>
          <form onClick={e => e.stopPropagation()} onSubmit={onSubmit} className={styles.transferModalLayout}>
            <span className={styles.transferModalCloseIcon} onClick={onCancel} />
            <div className={classNames(styles.modalContent, styles.center)}>
              <IconTransfer />
              <h3 className={styles.modalTitle}>Transfer</h3>
              <div className={styles.modalMessage}>
                Are you sure you want to transfer the thermostat to the other account?
              </div>
              <div className={styles.selectWrapper}>
                <SelectDynamic
                  values={selectedClient}
                  valueName=""
                  labelName=""
                  placeholder="Start typing the email or create a new one"
                  name="client"
                  searchFieldName="key"
                  responseType="stringArray"
                  searchFn={searchFn}
                  errorMessage={errorMessage}
                  useLazyQuery={useLazySearchClientEmailsQuery}
                  onChange={setSelectedClient}
                  backspaceDelete
                  requestOnSearchOnly
                  noOptionsMessage={renderNoOptionsMessage}
                  addCustomOptionRenderer={addCustomOptionRenderer}
                />
              </div>
            </div>
            <footer className={styles.transferModalFooter}>
              <ButtonNew onClick={onCancel} size="halfWidth" color="greyBordered">
                Cancel
              </ButtonNew>
              <ButtonNew
                onClick={onSubmit}
                size="halfWidth"
                type="submit"
                disabled={isConfirmDisabled || isTransferring}
                color="blue"
              >
                {isTransferring ? <Spinner /> : "Confirm"}
              </ButtonNew>
            </footer>
          </form>
        </>,
        document.body,
      )}
    </>
  )
}
